import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface AdoptButtonProps {
  officialId: string;
  initialAdoptionCount?: number;
  onAdoptionChange?: (isAdopted: boolean) => void;
}

export default function AdoptButton({
  officialId,
  initialAdoptionCount = 0,
  onAdoptionChange,
}: AdoptButtonProps) {
  const { token, isAuthenticated } = useAuth();
  const [isAdopted, setIsAdopted] = useState(false);
  const [adoptionCount, setAdoptionCount] = useState(initialAdoptionCount);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showShareCard, setShowShareCard] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      checkAdoptionStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [isAuthenticated, officialId]);

  const checkAdoptionStatus = async () => {
    try {
      const response = await fetch(`/api/adoptions/check/${officialId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdopted(data.isAdopted);
      }
    } catch (error) {
      console.error('Failed to check adoption status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleAdopt = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      if (isAdopted) {
        const response = await fetch(`/api/adoptions/${officialId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to unadopt');
        }

        setIsAdopted(false);
        setAdoptionCount(Math.max(0, adoptionCount - 1));
        onAdoptionChange?.(false);
      } else {
        const response = await fetch('/api/adoptions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ officialId }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to adopt');
        }

        setIsAdopted(true);
        setAdoptionCount(adoptionCount + 1);
        onAdoptionChange?.(true);
        setShowShareCard(true);
      }
    } catch (error: any) {
      console.error('Adoption error:', error);
      alert(error.message || 'Failed to update adoption');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = `${window.location.origin}/officials/${officialId}`;
  const shareText = `I just adopted a coward on AdoptACoward.com. Join me in holding our elected officials accountable: ${shareUrl}`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const bskyUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(shareText)}`;

  return (
    <>
      <button
        onClick={handleAdopt}
        disabled={loading || checkingStatus}
        className={`
          px-6 py-3 rounded-lg font-semibold transition-all
          ${
            isAdopted
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }
          ${loading || checkingStatus ? 'opacity-50 cursor-not-allowed' : ''}
          flex items-center gap-2
        `}
      >
        {checkingStatus ? (
          'Checking...'
        ) : loading ? (
          <>
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {isAdopted ? 'Unadopting...' : 'Adopting...'}
          </>
        ) : (
          <>
            {isAdopted ? (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Adopted
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Adopt
              </>
            )}
            <span className="text-sm opacity-75">({adoptionCount})</span>
          </>
        )}
      </button>

      {showShareCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowShareCard(false)}>
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <h2 className="text-xl font-black text-navy-700 dark:text-cream-100 uppercase tracking-tight">You adopted a coward</h2>
              <p className="text-sm text-navy-400 dark:text-cream-400 mt-1">
                Now spread the word. The more people watching, the more accountability.
              </p>
            </div>
            <div className="space-y-2 mb-4">
              <a href={tweetUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-navy-900 hover:bg-navy-800 text-white font-bold py-3 rounded-lg transition uppercase tracking-wider text-sm">
                Share on X / Twitter
              </a>
              <a href={bskyUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition uppercase tracking-wider text-sm">
                Share on Bluesky
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(shareText); }}
                className="block w-full text-center bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 font-bold py-3 rounded-lg transition uppercase tracking-wider text-sm"
              >
                Copy share text
              </button>
            </div>
            <button
              onClick={() => setShowShareCard(false)}
              className="block w-full text-center text-navy-400 dark:text-cream-400 text-xs font-bold hover:text-navy-600 dark:hover:text-cream-200 transition uppercase tracking-widest"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </>
  );
}
